import React, { useCallback, useRef, useState } from 'react';
import { kebabCase } from 'lodash';

import cn from '~/Utils/cn';

interface SecureIframeProps {
  html: string;
  className?: string;
  // Per-env image proxy origin (e.g. the Cloud Run service URI for go-camo).
  // When provided, added to the iframe CSP img-src so the rewriter's signed
  // URLs can load. Empty / undefined keeps the bucket-allowlist-only baseline.
  imageProxyOrigin?: string;
  // CSP img-src source for the same-origin inline-attachment endpoint that the
  // read-time `cid:` rewriter targets. Pass a path-scoped value
  // (`<origin>/api/v1/communications/`) so only attachment endpoints — not
  // arbitrary same-origin GETs — can load as images. When provided, appended
  // to img-src; empty / undefined keeps the bucket-allowlist-only baseline.
  attachmentOriginUrl?: string;
}

const iframeBodyStyleObject = {
  margin: 0,
  padding: 0,
  fontFamily: 'Poppins, sans-serif',
  color: '#202020',
  fontSize: '14px',
  lineHeight: '1.43',
  letterSpacing: '-0.3px',
};

const iframeBodyStyle = `${Object.entries(iframeBodyStyleObject)
  .map(([key, value]) => `${kebabCase(key)}: ${value};`)
  .join(';')}`;

// CSP img-src allowlist for fivesigma-controlled GCS buckets.
//
// storage.googleapis.com is multi-tenant, so a host-only allowlist would let
// any attacker host a tracking pixel in their own public bucket and bypass
// the iframe's protection. Path-scoping each entry to /<bucket>/ constrains
// the allowlist to objects under buckets we own.
//
// The set covers both the shared `public_files_support` bucket (used for
// fivesigma-managed template content) and every CMS env's App Engine bucket
// (per `.fivesigma-project.toml`). Historical signatures may reference any of
// these — including cross-env URLs created by config export/import — so all
// known buckets are allowlisted regardless of which env serves the page.
//
// Adding a non-fivesigma origin here requires a security review.
const FIVESIGMA_GCS_BUCKETS = [
  'public_files_support',
  'fivesigma-cms.appspot.com',
  'fivesigma-cms-dev.appspot.com',
  'fivesigma-cms-sandbox.appspot.com',
  'fivesigma-cms-uat.appspot.com',
  'fivesigma-cms-uat-au.appspot.com',
  'fivesigma-cms-eu.appspot.com',
  'fivesigma-cms-uk.appspot.com',
  'fivesigma-cms-au.appspot.com',
  'fivesigma-cms-xc-si.appspot.com',
];

// Static portion of the img-src allowlist — the buckets we always allow.
// The per-env image proxy origin is appended dynamically at render time
// when the `imageProxyOrigin` prop is set.
const STATIC_IMG_SRC_SOURCES = ['data:', ...FIVESIGMA_GCS_BUCKETS.map((b) => `https://storage.googleapis.com/${b}/`)];

const SecureIframe: React.FC<SecureIframeProps> = ({ html, className, imageProxyOrigin, attachmentOriginUrl }) => {
  const [height, setHeight] = useState<number | null>(null);
  const iframeRef = useRef<{
    iframeElement: HTMLIFrameElement | null;
    resizeObserver: ResizeObserver | null;
    previousWidth: number | null;
  }>({
    iframeElement: null,
    resizeObserver: null,
    previousWidth: null,
  });

  const measureAndSetHeight = useCallback(() => {
    const iframe = iframeRef.current.iframeElement;
    if (!iframe) return;

    try {
      const iframeBody = iframe.contentDocument?.body;
      if (!iframeBody) return;

      // adding 1px to the scrollHeight, as its value may be floored to the nearest integer,
      // which could result in a scrollbar appearing inside the iframe unnecessarily
      setHeight(iframeBody.scrollHeight + 1);
    } catch {
      // defaults to h-full
    }
  }, []);

  const handleLoad = useCallback(() => {
    measureAndSetHeight();
    // Re-measure after web fonts load — Poppins is loaded with display=swap,
    // so text may reflow when the font file arrives after the initial load event
    try {
      const fonts = iframeRef.current.iframeElement?.contentDocument?.fonts;
      fonts?.ready?.then(measureAndSetHeight);
    } catch {
      // contentDocument access may fail if sandbox changes
    }
  }, [measureAndSetHeight]);

  const cleanup = useCallback(() => {
    iframeRef.current.resizeObserver?.disconnect();
    iframeRef.current = {
      iframeElement: null,
      resizeObserver: null,
      previousWidth: null,
    };
  }, []);

  const iframeRefCallback = useCallback(
    (iframeElement: HTMLIFrameElement) => {
      if (!iframeElement) {
        cleanup();
      } else if (iframeRef.current.iframeElement !== iframeElement) {
        cleanup();

        const resizeObserver = new ResizeObserver(([iframeEntry]) => {
          if (iframeRef.current.previousWidth !== iframeEntry.contentRect.width) {
            iframeRef.current.previousWidth = iframeEntry.contentRect.width;
            measureAndSetHeight();
          }
        });
        resizeObserver.observe(iframeElement);
        iframeRef.current = { ...iframeRef.current, iframeElement, resizeObserver };
      }
    },
    [measureAndSetHeight, cleanup]
  );

  // Reject anything that could break out of the img-src directive (whitespace
  // would split it into two tokens, ';' would terminate the directive). The
  // value comes from server config, but defense-in-depth is cheap and rules
  // out a future bug elsewhere reaching the CSP via this seam.
  const safeProxyOrigin = imageProxyOrigin && !/[\s;]/.test(imageProxyOrigin) ? imageProxyOrigin : null;
  const safeAttachmentOrigin = attachmentOriginUrl && !/[\s;]/.test(attachmentOriginUrl) ? attachmentOriginUrl : null;
  const imgSrcSources = [
    ...STATIC_IMG_SRC_SOURCES,
    ...(safeProxyOrigin ? [safeProxyOrigin] : []),
    ...(safeAttachmentOrigin ? [safeAttachmentOrigin] : []),
  ];
  const csp = `
    default-src 'none';
    script-src 'none';
    style-src 'unsafe-inline' https://fonts.googleapis.com;
    img-src ${imgSrcSources.join(' ')};
    connect-src 'none';
    font-src https://fonts.gstatic.com;
    frame-ancestors 'none';
    form-action 'none';
    object-src 'none';
    media-src 'none';
  `;

  // Add target="_blank" and rel="noopener noreferrer" to all links for security
  const processedHtml = html.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
    // Check if target already exists
    const hasTarget = /target\s*=/i.test(attributes);
    const hasRel = /rel\s*=/i.test(attributes);

    let newAttributes = attributes;
    if (!hasTarget) {
      newAttributes += ' target="_blank"';
    }
    if (hasRel) {
      newAttributes = newAttributes.replace(
        /rel\s*=\s*(?:"([^"]*)"|'([^']*)'|(\S+))/i,
        (
          _: string,
          doubleQuoted: string | undefined,
          singleQuoted: string | undefined,
          unquoted: string | undefined
        ) => {
          const val = doubleQuoted ?? singleQuoted ?? unquoted ?? '';
          const parts = val.split(/\s+/).filter(Boolean);
          if (!parts.includes('noopener')) parts.push('noopener');
          if (!parts.includes('noreferrer')) parts.push('noreferrer');
          return `rel="${parts.join(' ')}"`;
        }
      );
    } else {
      newAttributes += ' rel="noopener noreferrer"';
    }

    return `<a ${newAttributes}>`;
  });

  const secureHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta http-equiv="Content-Security-Policy" content="${csp}">
        <meta charset="utf-8">
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap">
      </head>
      <body style="${iframeBodyStyle}">
        ${processedHtml}
      </body>
    </html>
  `;

  return (
    <iframe
      ref={iframeRefCallback}
      srcDoc={secureHtml}
      // SECURITY NOTE: allow-same-origin is fairly safe here because allow-scripts is NOT included
      // and the CSP has script-src 'none'. Without script execution, iframe content cannot
      // escape the sandbox. NEVER add allow-scripts — that combination is dangerous.
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      className={cn('h-full w-full border-none', className)}
      style={height ? { minHeight: `${height}px` } : {}}
      title="Secure Email Content"
      referrerPolicy="no-referrer"
      onLoad={handleLoad}
    />
  );
};

export default SecureIframe;
